import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import config from 'config'
import { AddPipeOpeningConfigRequest, PipeOpeningConfig } from 'pages/settings/configuration/ManagePipeOpeningsScreen'
import { useAuth0Authentication } from './useAuth0Authentication'

export interface IUsePipeOpenings {
    pipeOpenings: PipeOpeningConfig[] | undefined,
    isLoading: boolean,
    error: Error | null,
    deletePipeOpening: (itemToDelete: PipeOpeningConfig) => Promise<void>,
    isDeleting: boolean,
    deleteError: Error | null,
    addPipeOpening: (pipeOpening: AddPipeOpeningConfigRequest) => Promise<void>,
    isAdding: boolean,
    addError: Error | null,
    editPipeOpening: (pipeOpening: PipeOpeningConfig) => Promise<void>,
    isEditing: boolean,
    editError: Error | null
}

const fetchPipeOpenings = async (accessToken: string): Promise<PipeOpeningConfig[]> => {
    const response = await fetch(
        `${config.apiUrl}/configuration/pipe-openings`,
        {
            method: 'GET',
            redirect: 'follow',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": 'application/json'
            },
        }
    )

    if (!response.ok) {
        throw new Error(`Failed to fetch pipe openings: ${response.statusText}`)
    }

    const pipeOpeningsResp: PipeOpeningConfig[] = await response.json()

    return pipeOpeningsResp.sort((a, b) => a.material.localeCompare(b.material) || a.insideDiameter - b.insideDiameter)
}

// Separate API function for deleting pipe openings
const deletePipeOpeningAPI = async (accessToken: string, itemToDelete: PipeOpeningConfig): Promise<void> => {
    const response = await fetch(
        `${config.apiUrl}/configuration/pipe-openings`,
        {
            method: 'DELETE',
            redirect: 'follow',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": 'application/json'
            },
            body: JSON.stringify(itemToDelete)
        }
    )

    if (!response.ok) {
        throw new Error(`Failed to delete pipe opening: ${response.statusText}`)
    }
}

// Separate API function for adding pipe openings
const addPipeOpeningAPI = async (accessToken: string, pipeOpening: AddPipeOpeningConfigRequest): Promise<void> => {
    const response = await fetch(
        `${config.apiUrl}/configuration/pipe-openings`,
        {
            method: 'POST',
            redirect: 'follow',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": 'application/json'
            },
            body: JSON.stringify(pipeOpening)
        }
    )

    if (!response.ok) {
        throw new Error(`Failed to add pipe opening: ${response.statusText}`)
    }
}

// Separate API function for editing pipe openings
const editPipeOpeningAPI = async (accessToken: string, pipeOpening: PipeOpeningConfig): Promise<void> => {
    const response = await fetch(
        `${config.apiUrl}/configuration/pipe-openings`,
        {
            method: 'PATCH',
            redirect: 'follow',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": 'application/json'
            },
            body: JSON.stringify(pipeOpening)
        }
    )

    if (!response.ok) {
        throw new Error(`Failed to edit pipe opening: ${response.statusText}`)
    }
}

export const usePipeOpenings = (): IUsePipeOpenings => {
    const { getAuth0AccessToken } = useAuth0Authentication()
    const queryClient = useQueryClient()

    const {
        data: pipeOpenings,
        isLoading,
        error
    } = useQuery<PipeOpeningConfig[], Error>({
        queryKey: ['pipeOpenings'],
        queryFn: async () => {
            const accessToken = await getAuth0AccessToken()
            return fetchPipeOpenings(accessToken)
        }
    })

    const {
        mutateAsync: deletePipeOpening,
        isPending: isDeleting,
        error: deleteError
    } = useMutation<void, Error, PipeOpeningConfig>({
        mutationFn: async (itemToDelete: PipeOpeningConfig) => {
            const accessToken = await getAuth0AccessToken()
            return deletePipeOpeningAPI(accessToken, itemToDelete)
        },
        onSuccess: () => {
            // Invalidate and refetch pipe openings after successful deletion
            queryClient.invalidateQueries({ queryKey: ['pipeOpenings'] })
        }
    })

    const {
        mutateAsync: addPipeOpening,
        isPending: isAdding,
        error: addError
    } = useMutation<void, Error, PipeOpeningConfig>({
        mutationFn: async (pipeOpening: PipeOpeningConfig) => {
            const accessToken = await getAuth0AccessToken()
            return addPipeOpeningAPI(accessToken, pipeOpening)
        },
        onSuccess: () => {
            // Invalidate and refetch pipe openings after successful addition
            queryClient.invalidateQueries({ queryKey: ['pipeOpenings'] })
        }
    })

    const {
        mutateAsync: editPipeOpening,
        isPending: isEditing,
        error: editError
    } = useMutation<void, Error, PipeOpeningConfig>({
        mutationFn: async (pipeOpening: PipeOpeningConfig) => {
            const accessToken = await getAuth0AccessToken()
            return editPipeOpeningAPI(accessToken, pipeOpening)
        },
        onSuccess: () => {
            // Invalidate and refetch pipe openings after successful edit
            queryClient.invalidateQueries({ queryKey: ['pipeOpenings'] })
        }
    })

    return {
        pipeOpenings,
        isLoading,
        error: error || null,
        deletePipeOpening,
        isDeleting,
        deleteError: deleteError || null,
        addPipeOpening,
        isAdding,
        addError: addError || null,
        editPipeOpening,
        isEditing,
        editError: editError || null
    }
}